/**
 * پیگیری رنگرزی — ابزارهای مشترکِ صفحه: ورودیِ «گزینهٔ قبلی + تایپ»، ورودیِ عدد، ارسالِ PDF از گوشی.
 */
import React, { useMemo, useState } from "react";
import { AutoComplete, Input, Tag, message } from "antd";
import api from "../../api/client";
import { apiError } from "../../utils/apiError";
import { saveBlob } from "../../utils/downloadFile";
import { faDigits } from "../../utils/faNumberFormat";

export const BASE = "/api/v1/dye";
const FA_TO_EN = { "۰": "0", "۱": "1", "۲": "2", "۳": "3", "۴": "4", "۵": "5", "۶": "6", "۷": "7", "۸": "8", "۹": "9",
  "٠": "0", "١": "1", "٢": "2", "٣": "3", "٤": "4", "٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9" };
export const toEn = (s) => String(s ?? "").replace(/[۰-۹٠-٩]/g, (d) => FA_TO_EN[d]);
export const fa = (v) => faDigits(v ?? "");
/**
 * برچسبِ پارت/سفارش («1-8» = روز-ماه) برای خواندنِ راست‌به‌چپ: روز سمتِ راست، ماه سمتِ چپ.
 * کاربر (v2.169.0): «زده 8-1، بکنش 1-8». رشته معکوس و چپ‌به‌راست ایزوله می‌شود ⇒ چشمِ فارسی‌خوان از راست «۱-۸» می‌خواند.
 * پسوندِ « (2)» (سفارشِ دومِ همان پارت) سرِ جایش می‌ماند.
 */
export const lbl = (v) => {
  if (!/^[\d\-/ ()]*$/.test(String(v ?? ""))) return faDigits(v ?? "");   // «اول دوره …» یا کدِ متنی دست نمی‌خورد
  const m = String(v ?? "").match(/^(.*?)( \(\d+\))?$/);
  const base = m[1].split(/([-/])/).reverse().join("");
  return `\u2066${faDigits(base)}\u2069${faDigits(m[2] || "")}`;
};
export const n0 = (v) => Number(v) || 0;

/**
 * ورودیِ متن با گزینه‌های قبلی (کاربر: «وقتی روش کلیک می‌کند هم گزینهٔ قبلی را نشان بدهد، هم آمادهٔ تایپ باشد»).
 * با لمس، همهٔ گزینه‌ها باز می‌شوند؛ با تایپ فیلتر می‌شوند؛ مقدارِ تازه هم پذیرفته می‌شود (سرور یادش می‌گیرد).
 * `chips`: چند گزینهٔ پرکاربرد به‌صورتِ دکمه زیرِ کادر برای انتخابِ تک‌لمسی.
 */
export function QuickPick({ value, onChange, options = [], placeholder, numeric, chips = 0, onPickOption }) {
  const [typed, setTyped] = useState(false);
  const active = useMemo(() => options.filter((o) => o.active !== false), [options]);
  const shown = useMemo(() => {
    const q = toEn(value || "").trim();
    const list = typed && q ? active.filter((o) => toEn(o.value).includes(q)) : active;
    return list.map((o) => ({
      value: o.value,
      label: (
        <span className="dye-opt">
          <span>{fa(o.value)}</span>
          {o.code ? <span className="dye-opt-code">{fa(o.code)}</span> : null}
        </span>
      ),
    }));
  }, [active, typed, value]);
  const pick = (v) => {
    onChange?.(v);
    const o = active.find((x) => x.value === v);
    if (o) onPickOption?.(o);
  };
  return (
    <div className="dye-qp">
      <AutoComplete
        value={fa(value)}
        options={shown}
        onFocus={() => setTyped(false)}
        onSearch={() => setTyped(true)}
        onChange={(v) => onChange?.(toEn(v) || null)}
        onSelect={pick}
        style={{ width: "100%" }}
        popupMatchSelectWidth
      >
        <Input size="large" placeholder={placeholder} inputMode={numeric ? "decimal" : undefined} allowClear />
      </AutoComplete>
      {chips > 0 && active.length > 0 && (
        <div className="dye-chips">
          {active.slice(0, chips).map((o) => (
            <Tag.CheckableTag key={o.id} checked={value === o.value} onChange={() => pick(o.value)}>
              {fa(o.value)}
            </Tag.CheckableTag>
          ))}
        </div>
      )}
    </div>
  );
}

/** عددِ صحیح (طاقه) — ارقامِ فارسیِ صفحه‌کلیدِ گوشی هم پذیرفته می‌شود. */
export function IntInput({ value, onChange, placeholder, ...rest }) {
  return (
    <Input size="large" inputMode="numeric" placeholder={placeholder}
      value={value === null || value === undefined ? "" : fa(value)}
      onChange={(e) => {
        const s = toEn(e.target.value).replace(/[^\d]/g, "");
        onChange?.(s === "" ? null : Number(s));
      }} {...rest} />
  );
}

/** PDFِ سفارش را می‌گیرد (فایل آمادهٔ ارسال)؛ ارسال در کلیکِ بعدی انجام می‌شود (مرورگرِ گوشی اشتراک را فقط با لمسِ تازه می‌پذیرد). */
export async function fetchOrderPdf(order, page = "A4") {
  try {
    const r = await api.get(`${BASE}/orders/${order.id}/pdf`, { params: { page }, responseType: "blob" });
    const name = `سفارش-رنگرزی-${String(order.code).replace(/[^\w\u0600-\u06FF-]+/g, "_")}.pdf`;
    return { blob: r.data, file: new File([r.data], name, { type: "application/pdf" }), name };
  } catch (e) {
    message.error(apiError(e, "ساختِ PDF ناموفق بود"));
    return null;
  }
}

export const canShareFile = (file) => {
  try { return !!(file && navigator.canShare && navigator.canShare({ files: [file] })); } catch { return false; }
};

export async function shareOrSave(pdf, title) {
  if (!pdf) return;
  if (canShareFile(pdf.file)) {
    try { await navigator.share({ files: [pdf.file], title }); return; } catch (e) {
      if (e?.name === "AbortError") return;           // کاربر خودش بست
    }
  }
  saveBlob(pdf.blob, pdf.name);
  message.success("PDF دانلود شد");
}

/** مرحلهٔ پارت (به ترتیبِ طی‌شدن): خام ⇐ در رنگرزی ⇐ تحویل‌شده — برای فیلتر و چیدمانِ فهرست. */
export const STAGES = [
  { value: "raw", label: "خام" },
  { value: "dyeing", label: "در رنگرزی" },
  { value: "done", label: "تحویل‌شده" },
];
export const stageOf = (l) => (!l.ordered ? "raw" : l.delivery === "full" ? "done" : "dyeing");

export const STATUS = {
  ordered: { label: "در رنگرزی", color: "processing" },
  partial: { label: "تحویلِ ناقص", color: "warning" },
  delivered: { label: "تحویل شد", color: "success" },
};

/** وضعیتِ تحویلِ پارت (از سفارش‌هایش) — کاربر: «پارت را ببینیم تحویل شده یا نه». */
export const DELIVERY = {
  pending: { label: "در رنگرزی", color: "processing" },
  partial: { label: "تحویلِ ناقص", color: "warning" },
  full: { label: "تحویل کامل", color: "success" },
  none: { label: "", color: "default" },
};
